import { cn } from '@/lib/utils';
import {
  LayoutDashboard,
  Package,
  Upload,
  Settings,
  BeakerIcon,
  Users,
  BarChart2,
  FileText,
  PanelLeft,
  PanelRight,
  Pin,
  PinOff,
  ChevronDown,
  ChevronUp,
} from 'lucide-react';
import { NavLink, useLocation, Link } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { useState, useEffect } from 'react';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { productService } from '@/services/productService';
import { useNavigationTranslations } from '@/hooks/useNavigationTranslations';

// Define CSS variables for sidebar widths
const SIDEBAR_CSS = {
  '--sidebar-width': '16rem',
  '--sidebar-rail': '4rem',
} as React.CSSProperties;

interface SidebarProps {
  className?: string;
  isMobile?: boolean;
}

interface NavItemProps {
  icon: React.ElementType;
  label: string;
  href: string;
  badge?: string | number;
  requiredPermission?: string;
  collapsed?: boolean;
  isProducts?: boolean;
  end?: boolean;
}

// Cookie utility functions
const setCookie = (name: string, value: string, daysToExpire: number) => {
  const date = new Date();
  date.setTime(date.getTime() + daysToExpire * 24 * 60 * 60 * 1000);
  document.cookie = name + '=' + value + ';expires=' + date.toUTCString() + ';path=/';
};

const getCookie = (name: string) => {
  const cookieValue = document.cookie.match('(^|;)\\s*' + name + '\\s*=\\s*([^;]+)');
  return cookieValue ? decodeURIComponent(cookieValue.pop() || '') : '';
};

const NavItem = ({
  icon: Icon,
  label,
  href,
  badge,
  requiredPermission,
  collapsed,
  end,
}: NavItemProps & { end?: boolean }) => {
  const { checkPermission } = useAuth();

  // If a permission is required but user doesn't have it, don't show the item
  if (requiredPermission && !checkPermission(requiredPermission)) {
    return null;
  }

  const navLink = (
    <NavLink
      to={href}
      {...(end ? { end: true } : {})}
      className={({ isActive }) =>
        cn(
          'flex items-center rounded-md transition-colors',
          collapsed ? 'p-2.5 mx-auto justify-center' : 'px-3 py-2.5 justify-start w-full',
          isActive
            ? collapsed
              ? 'bg-primary-50'
              : 'bg-primary-50 text-primary-700 font-medium'
            : 'text-enterprise-600 hover:bg-enterprise-100'
        )
      }
    >
      {({ isActive }) => (
        <>
          <div
            className={cn(
              'flex items-center transition-transform duration-200 hover:scale-110',
              collapsed ? 'justify-center p-0.5' : 'gap-3'
            )}
          >
            <Icon
              size={collapsed ? 24 : 22}
              className={isActive ? 'text-primary-600' : 'text-enterprise-500'}
            />
            {!collapsed && <span>{label}</span>}
          </div>
          {badge && !collapsed && (
            <span className="ml-auto rounded-full bg-primary-50 px-2 py-0.5 text-xs font-medium text-primary-700">
              {badge}
            </span>
          )}
        </>
      )}
    </NavLink>
  );

  // Only show tooltip when collapsed AND not on mobile
  return collapsed && !window.matchMedia('(max-width: 768px)').matches ? (
    <TooltipProvider delayDuration={300}>
      <Tooltip>
        <TooltipTrigger asChild>{navLink}</TooltipTrigger>
        <TooltipContent
          side="right"
          sideOffset={4}
          className="rounded-md border border-enterprise-200 bg-white p-2 text-sm shadow-md"
        >
          <p className="font-medium">{label}</p>
          {badge && (
            <span className="ml-1 inline-block rounded-full bg-primary-50 px-2 py-0.5 text-xs font-medium text-primary-700">
              {badge}
            </span>
          )}
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  ) : (
    navLink
  );
};

export function Sidebar({ className, isMobile = false }: SidebarProps) {
  const { user, logout } = useAuth();
  const location = useLocation();
  const nav = useNavigationTranslations();
  const [pinState, setPinState] = useState<'open' | 'unpinned' | 'closed'>(() => {
    const savedState = getCookie('sidebar:pin');
    // If value is invalid or missing, default to "open"
    if (savedState === 'open' || savedState === 'unpinned' || savedState === 'closed') {
      return savedState as 'open' | 'unpinned' | 'closed';
    }
    // Set default cookie if no valid value exists
    document.cookie = `sidebar:pin=${encodeURIComponent('open')}; max-age=${7 * 24 * 60 * 60}; path=/`;
    return 'open';
  });
  const [hovering, setHovering] = useState(false);
  const [productsOpen, setProductsOpen] = useState(false);
  const [productCount, setProductCount] = useState<number | null>(null);

  // Fetch product count for sidebar badge
  useEffect(() => {
    let isMounted = true;
    productService
      .getStats()
      .then(res => {
        if (isMounted && res && typeof res.total_products === 'number') {
          setProductCount(res.total_products);
        }
      })
      .catch(() => {
        if (isMounted) setProductCount(null);
      });
    return () => {
      isMounted = false;
    };
  }, []);

  // Cycle through pin states (only on desktop)
  const cyclePinState = () => {
    if (isMobile) return; // No cycling on mobile

    setPinState(prev => {
      const next = prev === 'open' ? 'closed' : prev === 'closed' ? 'unpinned' : 'open';
      document.cookie = `sidebar:pin=${encodeURIComponent(next)}; max-age=${7 * 24 * 60 * 60}; path=/`;
      return next;
    });
  };

  // Handle keyboard shortcut
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      // ⌘B / Ctrl+B - only on desktop
      if (!isMobile && (e.metaKey || e.ctrlKey) && e.key === 'b') {
        e.preventDefault();
        cyclePinState();
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isMobile]);

  // Derive collapse state from pinState
  const isCollapsed = pinState === 'closed';
  const isExpanded = pinState === 'open';
  const isPreview = pinState === 'unpinned';

  // Always use full sidebar on mobile, and expand on hover for desktop preview mode
  const effectiveCollapsed = isMobile
    ? false
    : isCollapsed
      ? true
      : isExpanded
        ? false
        : // unpinned
          !hovering;

  // Define main navigation items
  const mainNavItems = [
    {
      icon: LayoutDashboard,
      label: nav.main.dashboard,
      href: '/app',
      requiredPermission: 'dashboard.view',
      end: true,
    },
    {
      icon: Package,
      label: nav.main.products,
      href: '/app/products',
      badge:
        !location.pathname.startsWith('/app/products') && productCount !== null ? productCount : '',
      requiredPermission: 'product.view',
    },
    {
      icon: BarChart2,
      label: nav.main.reports,
      href: '/app/reports',
      requiredPermission: 'dashboard.view',
    },
  ];

  // Define management navigation items
  const managementNavItems = [
    {
      icon: Upload,
      label: nav.management.uploadData,
      href: '/app/upload',
      requiredPermission: 'product.add',
    },
    {
      icon: FileText,
      label: nav.management.documentation,
      href: '/app/documentation',
    },
    {
      icon: Users,
      label: nav.management.team,
      href: '/app/team',
      requiredPermission: 'team.view',
    },
    {
      icon: Settings,
      label: nav.management.settings,
      href: '/app/settings',
      // No required permission - all authenticated users should access basic settings
    },
  ];

  return (
    <div
      className={cn('flex h-screen flex-col bg-card/95 border-r border-border pl-1', className)}
      style={{
        ...SIDEBAR_CSS,
        width: effectiveCollapsed ? 'var(--sidebar-rail)' : 'var(--sidebar-width)',
        transition: 'width 300ms ease-in-out',
      }}
      onMouseEnter={() => setHovering(true)}
      onMouseLeave={() => setHovering(false)}
    >
      {/* Dedicated Toggle Header */}
      <div className="flex h-14 items-center justify-center">
        <TooltipProvider delayDuration={300}>
          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                variant="ghost"
                size="lg"
                className="size-9"
                onClick={cyclePinState}
                aria-label={nav.getSidebarAriaLabel(pinState)}
                disabled={isMobile}
              >
                {pinState === 'unpinned' && <Pin size={22} />}
                {pinState === 'open' && <Pin size={22} className="fill-current" />}
                {pinState === 'closed' && <PinOff size={22} className="fill-current" />}
                <span className="sr-only">{nav.getSidebarAriaLabel(pinState)}</span>
              </Button>
            </TooltipTrigger>
            <TooltipContent
              side="right"
              className="rounded-md border border-border bg-popover p-2 text-sm shadow"
            >
              {nav.getPinStateTooltip(pinState)}
            </TooltipContent>
          </Tooltip>
        </TooltipProvider>

        {!effectiveCollapsed && (
          <h1 className="ml-1 flex items-center text-lg font-bold text-foreground">
            {nav.branding.title}
          </h1>
        )}
      </div>
      {/* Icon Rail / Navigation area */}
      <div
        className={cn(
          'flex-1 flex flex-col overflow-auto',
          effectiveCollapsed ? 'items-center py-12 space-y-12' : 'px-4 py-6'
        )}
      >
        {/* Main navigation */}
        {!effectiveCollapsed && (
          <p className="mb-3 px-3 text-xs font-medium uppercase tracking-wider text-muted-foreground">
            {nav.main.sectionTitle}
          </p>
        )}

        <div
          className={cn(
            'flex flex-col',
            effectiveCollapsed
              ? 'items-center space-y-8 mb-8'
              : 'items-start w-full space-y-1.5 mb-8'
          )}
        >
          {mainNavItems.map(item => (
            <NavItem
              key={item.href}
              icon={item.icon}
              label={item.label}
              href={item.href}
              badge={item.badge}
              requiredPermission={item.requiredPermission}
              collapsed={effectiveCollapsed}
              end={item.end}
            />
          ))}
        </div>

        {/* Group separator - only shown when collapsed */}
        {isCollapsed && <div className="mx-auto my-4 h-px w-8 bg-enterprise-200" />}

        {/* Management navigation */}
        {!effectiveCollapsed && (
          <p className="mb-3 mt-8 px-3 text-xs font-medium uppercase tracking-wider text-muted-foreground">
            {nav.management.sectionTitle}
          </p>
        )}

        <div
          className={cn(
            'flex flex-col',
            effectiveCollapsed
              ? 'items-center space-y-8 mb-8'
              : 'items-start w-full space-y-1.5 mb-8'
          )}
        >
          {managementNavItems.map(item => (
            <NavItem
              key={item.href}
              icon={item.icon}
              label={item.label}
              href={item.href}
              requiredPermission={item.requiredPermission}
              collapsed={effectiveCollapsed}
            />
          ))}
        </div>
      </div>{' '}
    </div>
  );
}
